import React, { useEffect, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { documentService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import ctaTypes from '../../../../enums/CTA.enums';
import Accordion, { TAccordionProps } from '../../../@shared/filters/components/accordion';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import Search from '../search';
import { QueryParamsConst } from '../../../../constants/query-params.constants';
import { DownloadBrochureListingModel } from '../../models/downloadBrochureListing.model';
import {
  TDownloadBrochureFilter,
  TDownloadBrochureListingFilters,
} from '../../types/downloadBrochures.types';
import { TDropdownProps } from '../../../@shared/filters/components/dropdown';
import { googleDownloadBrochureListingEvent } from '../../../../utils/analytics';
import { brochuresResultsOpen } from '../../../../constants/analytics.constants';

const MobileFilter: FC<{
  model: DownloadBrochureListingModel;
  selectedFilters: TDownloadBrochureListingFilters;
}> = ({ model, selectedFilters }) => {
  const [shouldOpenFilterModal, setShouldOpenFilterModal] = useState(false);
  const [selectedFiltersCopy, setSelectedFiltersCopy] = useState(selectedFilters);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const docElement = documentService();
    if (!docElement?.body) return;

    if (shouldOpenFilterModal) docElement.body.classList.add('body--with-filter-model');
    else docElement.body.classList.remove('body--with-filter-model');
  }, [shouldOpenFilterModal]);

  const { CATEGORIES, LINE, SERIES, PAGE } = QueryParamsConst;

  const getAccordionOptionsByInd = (
    filter?: TDownloadBrochureFilter,
    titleDictionaryKey?: string
  ): TAccordionProps['accordionOptions'] => {
    if (!filter) return [];
    return [
      { id: filter.filterType, title: t(titleDictionaryKey || '') },
      ...(filter?.value?.map((value) => ({ id: value, title: value })) || []),
    ];
  };

  const accordionsProps = [
    {
      filter: model.findFilterByName('categories'),
      queryKey: CATEGORIES,
      titleDictionaryKey: 'cnhi-download-brochure-listing-all-categories',
      id: 'categories-accordion',
      hidden: false,
      accordionTitle: (
        <DictionaryTerm keyword="cnhi-download-brochure-listing-category-filter-title" />
      ),
    },
    {
      filter: model.findFilterByName('line'),
      queryKey: LINE,
      titleDictionaryKey: 'cnhi-download-brochure-listing-all-lines',
      id: 'line-accordion',
      hidden: false,
      accordionTitle: <DictionaryTerm keyword="cnhi-download-brochure-listing-line-filter-title" />,
    },
    {
      filter: model.findFilterByName('series'),
      queryKey: SERIES,
      titleDictionaryKey: 'cnhi-download-brochure-listing-all-series',
      id: 'series-accordion',
      hidden: !selectedFilters[LINE],
      accordionTitle: (
        <DictionaryTerm keyword="cnhi-download-brochure-listing-series-filter-title" />
      ),
    },
  ];

  const handleOptionClick = (
    ind: number,
    queryKey: string,
    options: TDropdownProps['dropdownOptions']
  ) => {
    const filterValue = ind === 0 ? '' : (options?.[ind].id as string);
    const query =
      queryKey == CATEGORIES
        ? { categories: filterValue, line: '', series: '' }
        : queryKey == LINE
        ? { categories: '', line: filterValue, series: '' }
        : { series: filterValue };
    model.updateQueryByBulkKeys(navigate, query);
    model.resetSearch(navigate);
    googleDownloadBrochureListingEvent(brochuresResultsOpen, query.line, query.series, query.categories);
  };

  const revertToOldFilters = () => {
    if (!selectedFilters['SEARCH_BROCHURE'])
      model.updateQueryByBulkKeys(navigate, selectedFiltersCopy);
  };
  const toggleModal = () => setShouldOpenFilterModal(!shouldOpenFilterModal);

  return (
    <div className="product-download-brochure-listing-with-filter--mbl">
      <div
        className="download-brochure-filter-modal-switch"
        onClick={() => {
          toggleModal();
          revertToOldFilters();
        }}
      >
        <DictionaryTerm keyword="cnhi-download-brochure-listing-filter-title" />
        <Icon iconName="set-nav-services-funnel" size={iconSize['mbl']} />
      </div>
      {shouldOpenFilterModal && (
        <div className="download-brochure-filter-modal">
          <div className="download-brochure-filter-modal__header">
            <span className="download-brochure-filter-modal__title">
              <DictionaryTerm keyword="cnhi-download-brochure-listing-filter-title" />
            </span>
            <Icon
              iconName="action-close"
              color="white"
              size={iconSize['dsk']}
              onClick={() => {
                setShouldOpenFilterModal(false);
                revertToOldFilters();
              }}
            />
          </div>
          <div className="download-brochure-filter-modal__body">
            {accordionsProps.map(
              ({ filter, id, accordionTitle, queryKey, titleDictionaryKey, hidden }) => {
                const options = getAccordionOptionsByInd(filter, titleDictionaryKey);
                if (!options?.length || hidden) return null;
                return (
                  <Accordion
                    key={id}
                    accordionTitle={accordionTitle}
                    accordionOptions={options}
                    handleOptionClick={(ind) => handleOptionClick(ind, queryKey, options)}
                    selectedOptionInd={
                      (filter?.value?.findIndex((val) => val === selectedFilters[queryKey]) ?? -1) +
                      1
                    }
                  />
                );
              }
            )}
          </div>
          <div className="download-brochure-filter-modal__ctas">
            <span
              className={`cta ${ctaTypes['primaryWhite']}`}
              onClick={() => {
                model.resetFilter(navigate);
                setSelectedFiltersCopy({
                  [PAGE]: '',
                  [LINE]: '',
                  [SERIES]: '',
                  [CATEGORIES]: '',
                });
              }}
            >
              <DictionaryTerm keyword="cnhi-download-brochure-listing-reset-filters" />
            </span>
            <span
              className={`cta ${ctaTypes['primaryRed']}`}
              onClick={() => {
                setSelectedFiltersCopy(selectedFilters);
                toggleModal();
              }}
            >
              <DictionaryTerm keyword="cnhi-download-brochure-listing-apply-filters" />
            </span>
          </div>
        </div>
      )}
      <div className="download-brochure-filter__search">
        <Search model={model} selectedFilters={selectedFilters} />
      </div>
    </div>
  );
};

export default MobileFilter;
